import { useEffect, useMemo, useState } from 'react';
import {
  ASSET_TYPES,
  ASSET_TYPE_LABELS,
  type Asset,
  type AssetRuleOverrides,
  type AssetType,
  type TenantAlert,
} from '@ww/shared';
import { api, type AlertPolicyResponse, type AssetInput } from '../api';
import WeatherMap, { type MapDraft } from '../components/WeatherMap';
import { PageHeader, Toggle, numOrNull } from '../components/ui';
import { useLive } from '../hooks/useLive';
import { I, Icon, typeIcon } from '../lib/icons';
import { ST, computeAssetStatuses, errorMessage, eventOptions, isAdmin } from '../lib/status';

const RADII = [5, 10, 15, 25];
const EMPTY_ALERTS: TenantAlert[] = [];
const EMPTY_ASSETS: Asset[] = [];

interface FormState {
  id: string | null;
  name: string;
  type: AssetType;
  lat: string;
  lon: string;
  description: string;
  tags: string;
  radius: string;
  customEvents: boolean;
  eventTypes: string[];
  wind: string;
  hail: string;
  groupIds: string[];
  enabled: boolean;
  publicMap: boolean;
}

function formFromAsset(a: Asset): FormState {
  const [lon, lat] = a.location?.coordinates ?? [0, 0];
  const ro = a.ruleOverrides ?? {};
  return {
    id: a._id,
    name: a.name ?? '',
    type: (ASSET_TYPES as readonly string[]).includes(a.type) ? a.type : 'custom',
    lat: Number.isFinite(lat) ? lat.toFixed(5) : '',
    lon: Number.isFinite(lon) ? lon.toFixed(5) : '',
    description: a.description ?? '',
    tags: (a.tags ?? []).join(', '),
    radius: String(a.alertRadiusMiles ?? ''),
    customEvents: Array.isArray(ro.eventTypes),
    eventTypes: Array.isArray(ro.eventTypes) ? ro.eventTypes : [],
    wind: ro.windThresholdMph === null || ro.windThresholdMph === undefined ? '' : String(ro.windThresholdMph),
    hail: ro.hailThresholdIn === null || ro.hailThresholdIn === undefined ? '' : String(ro.hailThresholdIn),
    groupIds: a.notificationGroupIds ?? [],
    enabled: a.enabled !== false,
    // Absent means private. An asset saved before this existed must not become public
    // because the field was missing.
    publicMap: a.publicMap === true,
  };
}

function coordsLabel(a: Asset): string {
  const c = a.location?.coordinates;
  if (!c) return '—';
  return `${c[1].toFixed(4)}, ${c[0].toFixed(4)}`;
}

export default function Assets() {
  const { me, assets: liveAssets, dashboard, tenant, refetchAssets, refetchDashboard } = useLive();
  const admin = isAdmin(me?.user.role);
  const assets = liveAssets ?? EMPTY_ASSETS;
  const alerts = dashboard?.alerts ?? EMPTY_ALERTS;
  const statuses = useMemo(() => computeAssetStatuses(assets, alerts), [assets, alerts]);

  const [query, setQuery] = useState('');
  const [form, setForm] = useState<FormState | null>(null);
  const [placing, setPlacing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [policy, setPolicy] = useState<AlertPolicyResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .alertPolicy()
      .then((p) => {
        if (!cancelled) setPolicy(p);
      })
      .catch(() => {
        /* policy is optional for this page */
      });
    return () => {
      cancelled = true;
    };
  }, [dashboard?.tenant?._id]);

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const policyEvents = policy?.policy?.eventTypes ?? [];
  const groups = policy?.notificationGroups ?? [];
  const defaultRadius = policy?.policy?.defaultRadiusMiles ?? 10;
  const options = eventOptions(policyEvents);

  const q = query.trim().toLowerCase();
  const filtered = assets.filter(
    (a) =>
      !q ||
      a.name.toLowerCase().includes(q) ||
      (ASSET_TYPE_LABELS[a.type] ?? a.type).toLowerCase().includes(q) ||
      (a.tags ?? []).some((t) => t.toLowerCase().includes(q)),
  );
  const enabledCount = assets.filter((a) => a.enabled).length;

  const set = (patch: Partial<FormState>) => setForm((f) => (f ? { ...f, ...patch } : f));

  const openNew = () => {
    setError(null);
    setConfirmDelete(false);
    setForm({
      id: null,
      name: '',
      type: 'custom',
      lat: '',
      lon: '',
      description: '',
      tags: '',
      radius: String(defaultRadius),
      customEvents: false,
      eventTypes: [],
      wind: '',
      hail: '',
      groupIds: [],
      enabled: true,
      // Off for every new asset, always. Publishing a location is a separate, deliberate
      // decision, never something that rides along with creating the asset.
      publicMap: false,
    });
    setPlacing(true);
  };
  const openEdit = (a: Asset) => {
    setError(null);
    setConfirmDelete(false);
    setPlacing(false);
    setForm(formFromAsset(a));
  };
  const close = () => {
    setForm(null);
    setPlacing(false);
    setConfirmDelete(false);
    setError(null);
  };

  const effectiveEvents = form ? (form.customEvents ? form.eventTypes : policyEvents) : [];
  const toggleEvent = (ev: string) => {
    if (!form) return;
    const cur = new Set(effectiveEvents);
    if (cur.has(ev)) cur.delete(ev);
    else cur.add(ev);
    set({ customEvents: true, eventTypes: options.filter((o) => cur.has(o)) });
  };

  const save = async () => {
    if (!form || !admin) return;
    const lat = numOrNull(form.lat);
    const lon = numOrNull(form.lon);
    const radius = numOrNull(form.radius);
    if (!form.name.trim()) return setError('Name is required.');
    if (lat === null || lat < -90 || lat > 90 || lon === null || lon < -180 || lon > 180)
      return setError('Set a valid location: click the map or enter latitude (−90…90) and longitude (−180…180).');
    if (radius === null || radius < 0 || radius > 100) return setError('Alert radius must be between 0 and 100 miles.');
    const ruleOverrides: AssetRuleOverrides = {};
    if (form.customEvents) ruleOverrides.eventTypes = form.eventTypes;
    const wind = numOrNull(form.wind);
    const hail = numOrNull(form.hail);
    if (wind !== null) ruleOverrides.windThresholdMph = wind;
    if (hail !== null) ruleOverrides.hailThresholdIn = hail;
    const body: AssetInput = {
      name: form.name.trim(),
      type: form.type,
      lat,
      lon,
      description: form.description.trim(),
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      alertRadiusMiles: radius,
      ruleOverrides,
      notificationGroupIds: form.groupIds,
      enabled: form.enabled,
      publicMap: form.publicMap,
    };
    setBusy(true);
    setError(null);
    try {
      if (form.id) await api.updateAsset(form.id, body);
      else await api.createAsset(body);
      setNotice(form.id ? 'Asset saved. Alert evaluation will update shortly.' : 'Asset added and now monitored.');
      close();
      await Promise.all([refetchAssets(), refetchDashboard()]);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!form?.id || !admin) return;
    setBusy(true);
    try {
      await api.deleteAsset(form.id);
      setNotice('Asset deleted.');
      close();
      await Promise.all([refetchAssets(), refetchDashboard()]);
    } catch (e) {
      setError(errorMessage(e));
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (a: Asset, enabled: boolean) => {
    try {
      await api.updateAsset(a._id, { enabled });
      await Promise.all([refetchAssets(), refetchDashboard()]);
    } catch (e) {
      setNotice(null);
      setError(errorMessage(e));
    }
  };

  const onMapClick = (lat: number, lon: number) => {
    if (!placing || !form) return;
    set({ lat: lat.toFixed(5), lon: lon.toFixed(5) });
    setPlacing(false);
  };

  const onAssetMove = async (a: Asset, lat: number, lon: number) => {
    if (!admin) return;
    if (form?.id === a._id) set({ lat: lat.toFixed(5), lon: lon.toFixed(5) });
    try {
      await api.updateAsset(a._id, { lat, lon });
      setNotice(`${a.name} moved.`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      await Promise.all([refetchAssets(), refetchDashboard()]);
    }
  };

  const draftLat = form ? numOrNull(form.lat) : null;
  const draftLon = form ? numOrNull(form.lon) : null;
  const draft: MapDraft | null = useMemo(
    () =>
      form && !form.id && draftLat !== null && draftLon !== null ? { lat: draftLat, lon: draftLon, type: form.type } : null,
    [form, draftLat, draftLon],
  );
  const readOnly = !admin;

  return (
    <div className="page">
      <PageHeader
        title="Assets"
        sub={`${assets.length} assets · ${enabledCount} monitored · each asset has its own alert radius and event subscriptions`}
      >
        <label style={{ position: 'relative', width: 260 }}>
          <span className="sr-only">Search assets</span>
          <Icon d={I.search} size={18} style={{ position: 'absolute', left: 10, top: 11, color: '#9fb0c8' }} />
          <input
            className="fld"
            type="search"
            placeholder="Search assets"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: 36, height: 40 }}
          />
        </label>
        {admin && (
          <button type="button" className="btn btn-primary hov" onClick={openNew} style={{ fontSize: 15 }}>
            <Icon d={I.plus} size={18} strokeWidth={2.4} />
            <span>Add asset</span>
          </button>
        )}
      </PageHeader>

      {notice && (
        <div className="ok-box" role="status">
          {notice}
        </div>
      )}
      {error && !form && (
        <div className="err-box" role="alert">
          {error}
        </div>
      )}

      <div className="assets-grid">
        {!form && (
          <section className="panel" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <div
              className="lbl asset-head"
              style={{ borderBottom: '1px solid #1e2c46' }}
            >
              <span />
              <span>Asset</span>
              <span>Status</span>
              <span>Radius</span>
              <span>On</span>
            </div>
            <div className="scroll" style={{ flexGrow: 1, minHeight: 0 }}>
              {!liveAssets && <div className="empty">Loading assets…</div>}
              {liveAssets && filtered.length === 0 && (
                <div className="empty">{assets.length ? 'No assets match your search.' : 'No assets yet.'}</div>
              )}
              {filtered.map((a) => {
                const st = statuses.get(a._id) ?? (a.enabled ? ST.clear : ST.disabled);
                return (
                  <div
                    key={a._id}
                    className="asset-row"
                    style={{ borderBottom: '1px solid #18253c' }}
                  >
                    <span
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: '#16223a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon d={typeIcon(a.type)} size={20} style={{ color: st.color }} />
                    </span>
                    <button
                      type="button"
                      onClick={() => openEdit(a)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        minWidth: 0,
                        padding: '4px 0',
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, maxWidth: '100%' }}>
                        <span style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.name}
                        </span>
                        {/* So a glance down the list shows which locations are exposed. */}
                        {a.publicMap && (
                          <span className="tag tag-public" title="Published on the county's public map">
                            PUBLIC
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 12.5, color: '#9fb0c8' }}>
                        {ASSET_TYPE_LABELS[a.type] ?? a.type} ·{' '}
                        <span className="mono" style={{ fontSize: 11.5 }}>
                          {coordsLabel(a)}
                        </span>
                      </span>
                    </button>
                    <span style={{ fontSize: 13, color: st.color, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span>{st.label}</span>
                      {st.etaLabel && (
                        <span style={{ fontSize: 11.5, color: '#93a5c1' }} title="Estimated from the NWS storm track">
                          arriving {st.etaLabel}
                        </span>
                      )}
                    </span>
                    <span className="mono" style={{ fontSize: 13, color: '#d3ddeb' }}>
                      {a.alertRadiusMiles} mi
                    </span>
                    <Toggle
                      on={a.enabled}
                      label={`Monitor ${a.name}`}
                      onChange={admin ? (v) => void toggleEnabled(a, v) : undefined}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {form && (
          <section
            className="scroll"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              minHeight: 0,
              padding: '16px 18px',
              background: '#0f1a2d',
              border: '1px solid #2b4574',
              borderRadius: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, flexGrow: 1 }}>
                {readOnly ? 'Asset details' : form.id ? 'Edit asset' : 'Add asset'}
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={close}
                style={{
                  width: 36,
                  height: 36,
                  border: 'none',
                  background: 'transparent',
                  color: '#9fb0c8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon d={I.x} size={18} />
              </button>
            </div>
            {error && (
              <div className="err-box" role="alert">
                {error}
              </div>
            )}
            <fieldset disabled={readOnly || busy} style={{ margin: 0, padding: 0, border: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label className="field">
                <span className="lbl">Name</span>
                <input className="fld" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Water Tower #3" />
              </label>
              <div className="grid-2">
                <label className="field">
                  <span className="lbl">Type</span>
                  <select className="fld" value={form.type} onChange={(e) => set({ type: e.target.value as AssetType })}>
                    {ASSET_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {ASSET_TYPE_LABELS[t] ?? t}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="field" style={{ justifyContent: 'flex-end' }}>
                  <label className="check" style={{ height: 38 }}>
                    <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
                    Monitoring enabled
                  </label>
                </div>
              </div>
              <div className="field">
                <label className="check" style={{ alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={form.publicMap}
                    onChange={(e) => set({ publicMap: e.target.checked })}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    Show on public map
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      Publishes this location's name, type and exact position to anyone who has the
                      county's public map link. Off unless you turn it on.
                    </div>
                  </span>
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: 12, alignItems: 'end' }}>
                <label className="field">
                  <span className="lbl">Latitude</span>
                  <input
                    className="fld mono"
                    inputMode="decimal"
                    value={form.lat}
                    onChange={(e) => set({ lat: e.target.value })}
                    placeholder="36.8656"
                  />
                </label>
                <label className="field">
                  <span className="lbl">Longitude</span>
                  <input
                    className="fld mono"
                    inputMode="decimal"
                    value={form.lon}
                    onChange={(e) => set({ lon: e.target.value })}
                    placeholder="-87.4886"
                  />
                </label>
                {!readOnly && (
                  <button
                    type="button"
                    className="btn btn-sm hov"
                    aria-pressed={placing}
                    onClick={() => setPlacing((p) => !p)}
                    style={
                      placing
                        ? { height: 38, background: '#5aa9ff', color: '#07101d', border: '1px solid #5aa9ff' }
                        : { height: 38, background: '#0b1526', color: '#cfe1ff', border: '1px solid #37598f' }
                    }
                  >
                    <Icon d={I.crosshair} size={16} />
                    {placing ? 'Click the map…' : form.lat ? 'Move on map' : 'Place on map'}
                  </button>
                )}
              </div>
              <div className="field">
                <span className="lbl">Alert radius (miles)</span>
                <div role="group" aria-label="Alert radius" style={{ display: 'flex', gap: 6 }}>
                  {RADII.map((r) => {
                    const on = Number(form.radius) === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        aria-pressed={on}
                        onClick={() => set({ radius: String(r) })}
                        style={{
                          height: 36,
                          flexGrow: 1,
                          borderRadius: 7,
                          fontSize: 14,
                          background: on ? '#1b3157' : '#0b1526',
                          border: `1px solid ${on ? '#37598f' : '#2a3b58'}`,
                          color: on ? '#eef3fa' : '#9fb0c8',
                        }}
                      >
                        {r} mi
                      </button>
                    );
                  })}
                  <input
                    className="fld mono"
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    aria-label="Custom radius in miles"
                    value={form.radius}
                    onChange={(e) => set({ radius: e.target.value })}
                    style={{ width: 90, height: 36 }}
                  />
                </div>
              </div>
              <fieldset style={{ margin: 0, padding: 0, border: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <legend className="lbl" style={{ padding: 0, marginBottom: 6, display: 'flex', gap: 8, width: '100%' }}>
                  <span style={{ flexGrow: 1 }}>
                    Subscribed events {form.customEvents ? '(custom)' : '(inherits tenant policy)'}
                  </span>
                </legend>
                {form.customEvents && !readOnly && (
                  <button
                    type="button"
                    className="btn-link"
                    style={{ alignSelf: 'flex-start', marginBottom: 4 }}
                    onClick={() => set({ customEvents: false, eventTypes: [] })}
                  >
                    Reset to tenant policy
                  </button>
                )}
                <div className="grid-2" style={{ rowGap: 0 }}>
                  {options.map((ev) => (
                    <label key={ev} className="check" style={{ fontSize: 14 }}>
                      <input type="checkbox" checked={effectiveEvents.includes(ev)} onChange={() => toggleEvent(ev)} />
                      {ev}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="grid-2">
                <label className="field">
                  <span className="lbl">Wind threshold override (mph)</span>
                  <input
                    className="fld mono"
                    type="number"
                    min={0}
                    value={form.wind}
                    placeholder={`Tenant: ${policy?.policy?.windThresholdMph ?? '—'}`}
                    onChange={(e) => set({ wind: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span className="lbl">Hail threshold override (in)</span>
                  <input
                    className="fld mono"
                    type="number"
                    min={0}
                    step={0.25}
                    value={form.hail}
                    placeholder={`Tenant: ${policy?.policy?.hailThresholdIn ?? '—'}`}
                    onChange={(e) => set({ hail: e.target.value })}
                  />
                </label>
              </div>
              <fieldset style={{ margin: 0, padding: 0, border: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <legend className="lbl" style={{ padding: 0, marginBottom: 6 }}>
                  Notification groups
                </legend>
                {groups.length === 0 && <span className="muted" style={{ fontSize: 13.5 }}>No notification groups configured.</span>}
                {groups.map((g) => (
                  <label key={g.id} className="check">
                    <input
                      type="checkbox"
                      checked={form.groupIds.includes(g.id)}
                      onChange={(e) =>
                        set({
                          groupIds: e.target.checked ? [...form.groupIds, g.id] : form.groupIds.filter((x) => x !== g.id),
                        })
                      }
                    />
                    {g.name}
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      {(g.recipients ?? []).length} recipients
                    </span>
                  </label>
                ))}
              </fieldset>
              <div className="grid-2">
                <label className="field">
                  <span className="lbl">Tags</span>
                  <input className="fld" value={form.tags} onChange={(e) => set({ tags: e.target.value })} placeholder="utilities, critical" />
                </label>
                <label className="field">
                  <span className="lbl">Description</span>
                  <textarea
                    className="fld"
                    rows={2}
                    value={form.description}
                    onChange={(e) => set({ description: e.target.value })}
                    placeholder="Optional"
                    style={{ minHeight: 38 }}
                  />
                </label>
              </div>
            </fieldset>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                paddingTop: 12,
                borderTop: '1px solid #1e2c46',
                marginTop: 'auto',
                flexWrap: 'wrap',
              }}
            >
              {admin && form.id && !confirmDelete && (
                <button type="button" className="btn btn-danger hov" disabled={busy} onClick={() => setConfirmDelete(true)}>
                  Delete
                </button>
              )}
              {admin && form.id && confirmDelete && (
                <span role="group" aria-label="Confirm delete" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#ffb3b7' }}>
                  Delete this asset?
                  <button type="button" className="btn btn-sm btn-danger-solid" disabled={busy} onClick={() => void remove()}>
                    Confirm
                  </button>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </button>
                </span>
              )}
              <div style={{ flexGrow: 1 }} />
              <button type="button" className="btn btn-ghost hov" onClick={close}>
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {admin && (
                <button type="button" className="btn btn-primary hov" disabled={busy} onClick={() => void save()}>
                  {busy ? 'Saving…' : 'Save asset'}
                </button>
              )}
            </div>
          </section>
        )}

        <section className="panel" style={{ position: 'relative', minHeight: 360, overflow: 'hidden', background: '#0b1526' }}>
          <WeatherMap
            tenant={tenant}
            alerts={alerts}
            assets={assets}
            editable={admin}
            placing={admin && placing && !!form}
            draft={draft}
            selectedAssetId={form?.id ?? null}
            onMapClick={onMapClick}
            onAssetMove={(a, lat, lon) => void onAssetMove(a, lat, lon)}
            onAssetClick={(a) => {
              if (!placing) openEdit(a);
            }}
          />
        </section>
      </div>
    </div>
  );
}
